<template lang="pug">
    Tabs.meta-pixel
        template(slot="tabbar-buttons")
            el-tooltip(class="item" effect="dark" :content="$t('Dismiss')" placement="top")
                el-button(
                    circle
                    icon="el-icon-fa-times fas"
                    :disabled="saving"
                    @click="$router.back()"
                )
            el-tooltip(class="item" effect="dark" :content="$t('Save')" placement="top")
                el-button(
                    circle
                    type="success"
                    icon="el-icon-fa-save fas"
                    :loading="saving"
                    :disabled="saving"
                    @click="submit"
                )

        el-form(
            label-position="top"
            :model="store.form"
            :rules="store.rules"
            ref="form"
        )
            el-card(shadow="always" v-loading="store.loading")
                .section
                    .flex-row
                        el-form-item.grow(:label="$t('Pixel ID')" prop="pixel_id")
                            el-input(v-model="store.form.pixel_id" maxlength="64" show-word-limit)

                    .credential-row
                        .credential-field
                            el-form-item(:label="$t('Access Token')")
                                el-input(
                                    type="password"
                                    v-model="store.form.access_token"
                                    show-password
                                )

                        .credential-field
                            el-form-item(:label="$t('Test Event Code')")
                                el-input(v-model="store.form.test_event_code")

                    .switches-row
                        el-form-item(class="switch")
                            el-switch(
                                :active-text="$t('Enabled')"
                                v-model="store.form.is_enabled"
                            )
                        el-form-item(class="switch")
                            el-switch(
                                :active-text="$t('Conversions API')"
                                v-model="store.form.send_server_events"
                            )

                .section
                    h4.section-title {{ $t('Events') }}
                    .event-groups
                        .event-group(v-for="group in eventGroups" :key="group.title")
                            h5.event-group-title {{ group.title }}
                            .event(v-for="event in group.events" :key="event.key")
                                el-switch(:active-text="event.label" v-model="store.form[event.key]")
                                code.event-name {{ event.name }}

                el-alert(type="info" :closable="false" show-icon)
                    template(slot="title")
                        span {{ $t('Verify events in the') }}
                        |
                        a(href="https://www.facebook.com/events_manager" target="_blank" rel="noopener noreferrer") {{ $t('Meta Events Manager') }}
                        | . {{ $t('The grey name under each event is the fbq event name as it appears in Events Manager. Browser and server-side events are deduplicated via event_id.') }}

                el-collapse.setup-guide
                    el-collapse-item(:title="$t('Setup instructions')" name="setup")
                        ol.setup-steps
                            li
                                strong {{ $t('Get the Pixel ID') }}
                                p
                                    | {{ $t('Open the') }}
                                    |
                                    a(href="https://www.facebook.com/events_manager" target="_blank" rel="noopener noreferrer") {{ $t('Meta Events Manager') }}
                                    | , {{ $t('select your dataset or create a new one, and copy the numeric Pixel ID from the settings.') }}
                            li
                                strong {{ $t('Connect it here') }}
                                p {{ $t('Paste the ID above, switch "Enabled" on and save. That is enough for browser tracking.') }}
                            li
                                strong {{ $t('Server-side tracking (optional)') }}
                                p {{ $t('The Conversions API also reports events from our server, so they still arrive when an ad blocker stops the browser. In Events Manager open the dataset settings, generate an access token under Conversions API and paste it above.') }}
                                p.setup-note {{ $t('Both channels send the same event_id, so Meta counts each event only once.') }}
                            li
                                strong {{ $t('Check it works') }}
                                p {{ $t('Open your shop and accept the cookie banner. Browser events can be checked with the Meta Pixel Helper browser extension. For server events, enter a test event code above and watch the Test Events tab in Events Manager.') }}
                                p.setup-note {{ $t('Nothing is sent before the visitor accepts cookies. That is intended.') }}

            .form-buttons
                el-button(
                    round
                    icon="el-icon-fa-times fas"
                    :disabled="saving"
                    @click="$router.back()"
                ) {{ $t('Dismiss') }}
                el-button(
                    round
                    type="success"
                    icon="el-icon-fa-save fas"
                    :loading="saving"
                    :disabled="saving"
                    @click="submit"
                ) {{ $t('Save') }}
</template>

<i18n>
{
    "de": {
        "Pixel ID": "Pixel ID",
        "Access Token": "Access Token",
        "Test Event Code": "Test Event Code",
        "Enabled": "Aktiviert",
        "Conversions API": "Conversions API",
        "Events": "Events",
        "Discovery": "Entdeckung",
        "Product & Cart": "Produkt & Warenkorb",
        "Checkout": "Checkout",
        "Page View": "Seitenaufruf",
        "View Content": "Inhalt aufgerufen",
        "Add to Cart": "In den Warenkorb",
        "Initiate Checkout": "Checkout gestartet",
        "Search": "Suche",
        "Purchase": "Kauf",
        "Verify events in the": "Events prüfen im",
        "Meta Events Manager": "Meta Events Manager",
        "The grey name under each event is the fbq event name as it appears in Events Manager. Browser and server-side events are deduplicated via event_id.": "Der graue Name unter jedem Event ist der fbq-Eventname, wie er im Events Manager erscheint. Browser- und server-seitige Events werden über die event_id dedupliziert.",
        "Dismiss": "Abbrechen",
        "Save": "Speichern",
        "Form is not valid!": "Formular ist ungültig!",
        "Correct your input and submit again.": "Bitte Eingabe korrigieren und erneut speichern.",
        "Meta Pixel saved successfully!": "Meta Pixel erfolgreich gespeichert!",
        "Meta Pixel could not be saved.": "Meta Pixel konnte nicht gespeichert werden.",
        "Setup instructions": "Einrichtung",
        "Get the Pixel ID": "Pixel-ID holen",
        "Open the": "Öffnen Sie den",
        "select your dataset or create a new one, and copy the numeric Pixel ID from the settings.": "wählen Sie Ihren Datensatz aus oder legen Sie einen neuen an und kopieren Sie die numerische Pixel-ID aus den Einstellungen.",
        "Connect it here": "Hier verbinden",
        "Paste the ID above, switch \"Enabled\" on and save. That is enough for browser tracking.": "ID oben einfügen, \"Aktiviert\" einschalten und speichern. Für das Browser-Tracking reicht das aus.",
        "Server-side tracking (optional)": "Server-seitiges Tracking (optional)",
        "The Conversions API also reports events from our server, so they still arrive when an ad blocker stops the browser. In Events Manager open the dataset settings, generate an access token under Conversions API and paste it above.": "Die Conversions API meldet Events zusätzlich von unserem Server, sodass sie auch ankommen, wenn ein Adblocker den Browser stoppt. Im Events Manager in den Datensatz-Einstellungen unter Conversions API ein Zugriffstoken erzeugen und oben einfügen.",
        "Both channels send the same event_id, so Meta counts each event only once.": "Beide Wege senden dieselbe event_id, daher zählt Meta jedes Event nur einmal.",
        "Check it works": "Funktion prüfen",
        "Open your shop and accept the cookie banner. Browser events can be checked with the Meta Pixel Helper browser extension. For server events, enter a test event code above and watch the Test Events tab in Events Manager.": "Shop öffnen und Cookie-Hinweis akzeptieren. Browser-Events lassen sich mit der Browser-Erweiterung Meta Pixel Helper prüfen. Für Server-Events oben einen Test Event Code eintragen und im Events Manager den Tab Testereignisse beobachten.",
        "Nothing is sent before the visitor accepts cookies. That is intended.": "Vor der Zustimmung des Besuchers wird nichts gesendet. Das ist so gewollt."
    }
}
</i18n>

<script>
import Tabs from '@/views/cms/Marketing/Partials/Tabs';
import { useMarketingMetaPixelStore } from '@/stores';

export default {
    name: 'MetaPixelForm',

    components: {
        Tabs,
    },

    data() {
        return {
            saving: false,
        };
    },

    computed: {
        store() {
            return useMarketingMetaPixelStore();
        },

        // Same journey grouping as the Google Tag Manager page, so the two
        // marketing tabs read the same way. The name shown is the fbq event
        // name, which is what appears in Meta's Events Manager.
        eventGroups() {
            return [
                {
                    title: this.$t('Discovery'),
                    events: [
                        { key: 'track_page_view', label: this.$t('Page View'), name: 'PageView' },
                        { key: 'track_search', label: this.$t('Search'), name: 'Search' },
                    ],
                },
                {
                    title: this.$t('Product & Cart'),
                    events: [
                        { key: 'track_view_content', label: this.$t('View Content'), name: 'ViewContent' },
                        { key: 'track_add_to_cart', label: this.$t('Add to Cart'), name: 'AddToCart' },
                    ],
                },
                {
                    title: this.$t('Checkout'),
                    events: [
                        { key: 'track_initiate_checkout', label: this.$t('Initiate Checkout'), name: 'InitiateCheckout' },
                        { key: 'track_purchase', label: this.$t('Purchase'), name: 'Purchase' },
                    ],
                },
            ];
        },
    },

    created() {
        this.store.fetch().then(() => {
            this.$nextTick(() => {
                if (this.$refs.form) {
                    this.$refs.form.clearValidate();
                }
            });
        });
    },

    methods: {
        submit() {
            this.$refs.form.validate((valid) => {
                if (!valid) {
                    this.$notify.warning({
                        title: this.$t('Form is not valid!'),
                        message: this.$t('Correct your input and submit again.'),
                        iconClass: 'fas el-icon-fa-exclamation-circle',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                    return;
                }
                this.saving = true;
                this.store.save()
                    .then(() => {
                        this.$notify.success({
                            title: this.$t('Meta Pixel saved successfully!'),
                            iconClass: 'fas el-icon-fa-check',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 4000,
                        });
                    })
                    .catch(() => {
                        this.$notify.error({
                            title: this.$t('Meta Pixel could not be saved.'),
                            iconClass: 'fas el-icon-fa-xmark',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        });
                    })
                    .finally(() => {
                        this.saving = false;
                    });
            });
        },
    },
};
</script>

<style lang="sass" scoped>
.meta-pixel
    .section
        &:not(:last-child)
            margin-bottom: 24px
    .section-title
        color: $color-grey-text
        margin: 0
        font-size: 13px
        font-weight: 600
        text-transform: uppercase
        letter-spacing: .5px
    .flex-row
        display: flex
        align-items: flex-end
        gap: 1rem
        .grow
            flex-grow: 1
    .switches-row
        display: flex
        align-items: center
        gap: 2rem
        margin-top: 4px
        margin-bottom: 12px
        .el-form-item
            margin-bottom: 0

    // Three journey stages side by side, so the toggles use the full card width
    // instead of stacking into the left third.
    .event-groups
        display: grid
        grid-template-columns: repeat(3, minmax(0, 1fr))
        gap: 8px 32px
        margin-top: 16px
    .event-group-title
        color: $color-grey-text
        margin: 0 0 12px
        padding-bottom: 8px
        border-bottom: 1px solid #EBEEF5
        font-size: 11px
        font-weight: 600
        text-transform: uppercase
        letter-spacing: .6px
    .event
        margin-bottom: 14px
        &:last-child
            margin-bottom: 0
    .event-name
        display: block
        // Aligns under the switch label: 40px switch + 10px Element gap.
        padding-left: 50px
        margin-top: 1px
        color: $color-grey-text
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace
        font-size: 11px
        line-height: 1.3

    @media (max-width: 1400px)
        .event-groups
            grid-template-columns: repeat(2, minmax(0, 1fr))
            gap: 20px 32px
    @media (max-width: 900px)
        .event-groups
            grid-template-columns: minmax(0, 1fr)
    .credential-row
        display: flex
        gap: 2rem
        margin-top: 8px
        .credential-field
            flex: 1
            min-width: 0
            .el-form-item
                margin-bottom: 0
    .el-alert
        margin-top: 16px
        a
            color: #409EFF
            text-decoration: underline
            &:before
                content: ' '
    .setup-guide
        margin-top: 8px
        border-top: none
        border-bottom: none
        // ::v-deep is required: these are inner nodes of ElCollapseItem, so the
        // scoped attribute never lands on them and globals.sass wins instead
        // (`.el-collapse-item__header { font-size: 1rem }`).
        ::v-deep .el-collapse-item__header
            font-size: 13px
            font-weight: 600
            color: $color-grey-text
            text-transform: uppercase
            letter-spacing: .5px
        ::v-deep .el-collapse-item__content
            padding-bottom: 12px
    .setup-steps
        margin: 0
        padding-left: 18px
        color: $color-grey-text
        font-size: 13px
        line-height: 1.5
        li
            margin-bottom: 14px
            &:last-child
                margin-bottom: 0
        strong
            color: #303133
        p
            margin: 2px 0 0
        a
            color: #409EFF
            text-decoration: underline
    .setup-note
        font-style: italic
        opacity: .85
    .form-buttons
        margin-top: 16px
        display: flex
        justify-content: flex-end
        gap: 8px
</style>
